import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMediaQuery } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { Meta } from 'src/components/Meta';
import { SearchParamsPagination } from 'src/components/Pagination';
import { Scroller } from 'src/components/Scroller';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Stack } from 'src/components/Stack';
import { DataTable } from 'src/components/Table/DataTable';
import { Theme } from 'src/components/muiTheme/types';
import { useSession } from 'src/contexts/SessionContext';
import { Video } from 'src/models/video/_types';
import {
  deleteGroupVideo,
  fetchGroupVideosByGroupId,
} from 'src/models/video/video';
import { AdaptiveSideMenu } from 'src/shared/AdaptiveSideMenu/AdaptiveSideMenu';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { Filters } from 'src/shared/Filters/Filters';
import { TagsFilter } from 'src/shared/Filters/TagsFilter';
import { formatYearMonthDay } from 'src/utils/dateFormatter';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { useGroupNavigation } from './GroupEditPage/hooks/useGroupNavigation';

export const GroupVideosPage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { session } = useSession();
  const [confirmProps, confirm] = useConfirmDialogComponent();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { getNavigationItems, getPageConfig } = useGroupNavigation(groupId);

  const {
    params,
    updateParam,
    filterParams,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams();

  const search = params.search;
  const page = Number(params.page || '1');
  const tags = filterParams.tags?.values || [];

  // Fetch videos with filters
  const { data, isLoading } = useQuery(
    [
      'groupVideos',
      groupId,
      page,
      search,
      tags,
      params.sortBy,
      params.sortDir,
      params.perPage,
    ],
    () =>
      fetchGroupVideosByGroupId({
        groupId: groupId!,
        search,
        tags,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
        page,
        perPage: params.perPage,
      }),
    {
      enabled: !!groupId,
    }
  );

  const groupVideos = data?.groupVideos || [];
  const total = data?.total || 0;

  // Delete video mutation
  const deleteVideoMutation = useMutation(deleteGroupVideo, {
    onSuccess: () => {
      queryClient.invalidateQueries(['groupVideos', groupId]);
      toast.success(
        t('group.Delete Success', {
          name: t('group.video.one'),
        })
      );
    },
    onError: () => {
      toast.error(
        t('group.Delete Error', {
          name: t('group.video.one').toLowerCase(),
        })
      );
    },
  });

  // Handle video deletion
  const handleDeleteVideo = async (video: Video) => {
    const confirmed = await confirm({
      title: t('group.Delete Title', {
        name: t('group.video.one'),
      }),
      message: t('group.Delete Message', {
        name: t('group.video.one').toLowerCase(),
      }),
      confirmText: t('group.Delete'),
      cancelText: t('group.Cancel'),
    });

    if (confirmed && video.id) {
      deleteVideoMutation.mutate({ groupVideoId: video.id });
    }
  };

  const delayIntervalText = useCallback(
    (delayInterval: number, expiresAt: number) => {
      if (delayInterval) {
        if (delayInterval > 200) {
          return t('Released on', {
            date: formatYearMonthDay(delayInterval),
          });
        }
        return t('Released on Week', {
          week: delayInterval + 1,
        });
      }
      if (expiresAt) {
        return t('Expires on', {
          date: formatYearMonthDay(expiresAt),
        });
      }

      return t('Published');
    },
    [t]
  );

  const setSortBy = (newValue: any) => {
    updateParam('sortBy', newValue);
  };

  const setSortDir = (newValue: any) => {
    updateParam('sortDir', newValue);
  };

  const columns = [
    {
      id: 'thumbnail',
      label: t('group.Thumbnail'),
      sortable: false,
      format: (_, row) => {
        return (
          <img
            src={row.thumbnailUrl}
            alt={row.name}
            style={{ width: '75px' }}
          />
        );
      },
    },
    {
      id: 'name',
      label: t('group.Video Name'),
      sortable: true,
    },
    {
      id: 'published',
      label: t('group.Published / Delay'),
      sortable: false,
      format: (_, row) => {
        return delayIntervalText(row.delayInterval, row.expiresAt);
      },
    },
    {
      id: 'createdAt',
      label: t('group.Created At'),
      sortable: true,
      hidden: smDown,
      format: (_, row) => {
        return formatYearMonthDay(row.createdAt);
      },
    },
    {
      id: 'tags',
      label: t('group.Tags'),
      sortable: false,
      hidden: smDown,
      format: (_, row) => {
        return row.tags.join(', ');
      },
    },
  ].filter((col) => col.hidden !== true);

  const rowActions = [
    {
      label: t('group.Edit Video'),
      icon: icon({ name: 'pen', style: 'regular' }),
      onClick: (video: Video) => {
        navigate(`/groups/${groupId}/videos/${video.id}`);
      },
    },
    {
      label: t('group.Delete Video'),
      icon: icon({ name: 'trash', style: 'regular' }),
      onClick: (video: Video) => handleDeleteVideo(video),
    },
  ];

  const filters = [
    {
      field: 'tags',
      label: t('group.Tags'),
      Component: (props: any) => (
        <TagsFilter {...props} type="video" showMatchAll={false} />
      ),
    },
  ];

  const navigationItems = getNavigationItems();
  const pageConfig = getPageConfig(false); // Always editing for videos page

  return (
    <>
      <Meta title={pageConfig.title} brand={session?.brand} />
      <AppFrameConfig
        id={'group-videos'}
        containerMaxWidth={'xl'}
        backButtonTitle={'Groups'}
        backButtonLinkTo={'/groups'}
        title={pageConfig.title}
        actions={pageConfig.actions}
      />

      <Stack direction={mdDown ? 'column' : 'row'}>
        <AdaptiveSideMenu
          navItems={navigationItems}
          activeValue="videos"
          routerEnabled={true}
          collapsible={true}
          title={t('group.editPage.Group Sections')}
          id="group-sections"
        />
        <Box sx={{ width: '100%', transition: 'all 0.3s ease' }}>
          <SectionToolbar
            sx={{ pb: 3, pt: 0, px: 0, justifyContent: 'space-between' }}
          >
            <SearchParamSearchTextField
              params={params}
              updateParam={updateParam}
              sx={{ width: smDown ? '100%' : '280px' }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                <Icon icon={icon({ name: 'plus', style: 'regular' })} />
              }
              onClick={() => {
                navigate(`/groups/${groupId}/videos/new`);
              }}
            >
              {t('group.Add New Video')}
            </Button>
          </SectionToolbar>
          <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
            <Scroller
              direction="row"
              spacing={2}
              hideScrollbar
              sx={{ mx: -2, px: 2, boxSizing: 'content-box' }}
            >
              <Filters
                filters={filters}
                params={params}
                updateParam={updateParam}
                updateFilterParam={updateFilterParam}
                getFilterParamValue={getFilterParamValue}
                resetFilterParams={resetFilterParams}
              />
            </Scroller>
          </SectionToolbar>
          <Card sx={{ mb: 6 }}>
            <DataTable
              fixedActions
              columns={columns}
              rows={groupVideos}
              sortBy={params.sortBy}
              setSortBy={setSortBy}
              sortDir={params.sortDir}
              setSortDir={setSortDir}
              onClickRow={(row) => {
                navigate(`/groups/${groupId}/videos/${row.id}`);
              }}
              noResultsText={
                search || tags.length > 0
                  ? t('group.no search video results', {
                      search: search ? `- "${search}"` : '',
                    })
                  : t('group.no video results')
              }
              loading={isLoading}
              totalItems={total}
              itemsPerPage={params.perPage}
              rowActions={rowActions}
            />
            <SearchParamsPagination
              total={total}
              params={params}
              updateParam={updateParam}
            />
          </Card>
        </Box>
      </Stack>
      <ConfirmDialog {...confirmProps} />
    </>
  );
};
